import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Eye, EyeOff, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// HaulLogix Logo Component - Shield Logo
const HaulLogixLogo = () => (
  <svg width="48" height="54" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="registerShieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="40%" stopColor="#3B82F6" />
        <stop offset="70%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="registerInnerShield" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="100%" stopColor="#0F172A" />
      </linearGradient>
      <linearGradient id="registerBlueBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="30%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
      <linearGradient id="registerGreenBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4ADE80" />
        <stop offset="30%" stopColor="#22C55E" />
        <stop offset="100%" stopColor="#15803D" />
      </linearGradient>
    </defs>
    <path d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" fill="url(#registerShieldBorder)"/>
    <path d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" fill="url(#registerInnerShield)"/>
    <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#registerBlueBar)" />
    <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#registerGreenBar)" />
    <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
  </svg>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    phone: '',
    role: '',
    service_type: '',
    mc_number: '',
    dot_number: '',
    region: '',
    equipment_type: '',
  });

  const roles = [
    { value: 'carrier', label: 'Carrier', description: 'Transport loads for brokers and shippers' },
    { value: 'owner_operator', label: 'Owner Operator', description: 'Independent truck owner-operator' },
    { value: 'fleet', label: 'Fleet', description: 'Manage multiple trucks and drivers' },
    { value: 'dispatcher', label: 'Dispatcher', description: 'Manage loads for carriers' },
    { value: 'broker', label: 'Broker', description: 'Post loads and connect carriers with shippers' },
    { value: 'shipper', label: 'Shipper', description: 'Ship goods and find carriers' },
    { value: 'dealer', label: 'Dealer', description: 'Auto dealership needing transport' },
    { value: 'auction', label: 'Auction', description: 'Auction house needing vehicle transport' },
    { value: 'rental_fleet_owner', label: 'Rental/Fleet Owner', description: 'Rental company or fleet management' },
  ];

  const serviceTypes = [
    { value: 'auto_transport', label: 'Auto Transport' },
    { value: 'hotshot', label: 'Hotshot' },
    { value: 'freight', label: 'Freight' },
    { value: 'multi_service', label: 'Multi-Service' },
  ];

  const equipmentTypes = [
    { value: 'open_carrier', label: 'Open Carrier' },
    { value: 'enclosed_carrier', label: 'Enclosed Carrier' },
    { value: 'hotshot', label: 'Hotshot Trailer' },
    { value: 'driveaway', label: 'Driveaway' },
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'dry_van', label: 'Dry Van' },
    { value: 'reefer', label: 'Reefer' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Step 2 required fields
    if (step === 2) {
      if (!formData.email || !formData.password || !formData.full_name || !formData.company_name) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      setStep(3);
      return;
    }
    
    // Validate Step 3 required fields
    if (step === 3) {
      if (!formData.mc_number || !formData.dot_number) {
        toast.error('MC Number and DOT Number are required');
        return;
      }
    }

    setLoading(true);
    try {
      // Clean up empty strings
      const cleanedData = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '')
      );
      
      await register(cleanedData);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Select Your Role</h3>
              <p className="text-sm text-gray-500">Choose how you'll use HaulLogix</p>
            </div>
            <div className="grid gap-3">
              {roles.map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, role: role.value });
                    setStep(2);
                  }}
                  data-testid={`role-${role.value}`}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    formData.role === role.value
                      ? 'border-[#4ADE80] bg-[#4ADE80]/10'
                      : 'border-[#3B82F6]/20 hover:border-[#3B82F6]/40 bg-[#0A1628]'
                  }`}
                >
                  <div className="font-medium text-white">{role.label}</div>
                  <div className="text-sm text-gray-500">{role.description}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="full_name"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                data-testid="register-fullname-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="company_name"
                placeholder="Your Company LLC"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                data-testid="register-company-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="register-phone-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Service Type</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => setFormData({ ...formData, service_type: value })}
              >
                <SelectTrigger className="h-12 bg-[#0A1628] border-[#3B82F6]/20" data-testid="register-service-type">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mc_number">MC Number <span className="text-red-500">*</span></Label>
              <Input
                id="mc_number"
                placeholder="MC123456"
                value={formData.mc_number}
                onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
                required
                data-testid="register-mc-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dot_number">DOT Number <span className="text-red-500">*</span></Label>
              <Input
                id="dot_number"
                placeholder="DOT123456"
                value={formData.dot_number}
                onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
                required
                data-testid="register-dot-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>

            {['carrier', 'owner_operator', 'fleet'].includes(formData.role) && (
              <div className="space-y-2">
                <Label>Equipment Type</Label>
                <Select
                  value={formData.equipment_type}
                  onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                >
                  <SelectTrigger className="h-12 bg-[#0A1628] border-[#3B82F6]/20" data-testid="register-equipment-type">
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                    {equipmentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="region">Operating Region</Label>
              <Input
                id="region"
                placeholder="e.g., Southeast US, Nationwide"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                data-testid="register-region-input"
                className="h-12 bg-[#0A1628] border-[#3B82F6]/20 focus:border-[#3B82F6]"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] px-4 py-20">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <HaulLogixLogo />
            <span className="text-2xl font-bold tracking-wider uppercase">
              <span className="text-white">HAUL</span><span className="text-[#4ADE80]">LOGIX</span>
            </span>
          </Link>
        </div>

        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]/90 backdrop-blur-sm" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)'}}>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold uppercase font-['Poppins'] tracking-wide">
              Create Account
            </CardTitle>
            <CardDescription>
              Step {step} of 3: {step === 1 ? 'Select Role' : step === 2 ? 'Account Details' : 'Business Info'}
            </CardDescription>
            {/* Progress bar */}
            <div className="flex gap-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-[#4ADE80]' : 'bg-[#1E3A5F]'
                  }`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              {renderStep()}

              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 h-12 border-[#3B82F6] bg-[#1D4ED8]/30 text-white hover:bg-[#3B82F6]/30"
                    data-testid="register-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    type="submit"
                    className="flex-1 h-12 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black uppercase tracking-wider font-semibold"
                    style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                    disabled={step === 1 && !formData.role}
                    data-testid="register-next-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="flex-1 h-12 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black uppercase tracking-wider font-semibold"
                    style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-[#4ADE80] hover:text-[#86EFAC] hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
